import { createApi } from '@reduxjs/toolkit/query/react';
import { DealerLocation } from '../../types';
import { LocationService } from './location-service';
import { baseQueryStub } from '../../utils';

// Service singleton helper
let locationService: LocationService | null = null;
const getLocationService = () => {
  locationService ??= new LocationService();
  return locationService;
};

export const locationsApi = createApi({
  reducerPath: 'locationsApi',
  baseQuery: baseQueryStub,
  tagTypes: ['Location'],
  endpoints: builder => ({
    getLocations: builder.query<DealerLocation[], number>({
      queryFn: async dealerId => {
        const service = getLocationService();
        const data = await service.getLocations(dealerId);
        return { data };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Location' as const, id })),
              { type: 'Location', id: 'LIST' },
            ]
          : [{ type: 'Location', id: 'LIST' }],
      keepUnusedDataFor: 30, // seconds
    }),
    getLocationById: builder.query<DealerLocation, string>({
      queryFn: async id => {
        const service = getLocationService();
        const data = await service.getLocationById(id);
        return { data };
      },
      providesTags: (_, __, id) => [{ type: 'Location', id }],
    }),
    addLocation: builder.mutation<DealerLocation, { locationData: Omit<DealerLocation, 'id'> }>({
      queryFn: async ({ locationData }) => {
        const service = getLocationService();
        const data = await service.createLocation(locationData);
        return { data };
      },
      invalidatesTags: [{ type: 'Location', id: 'LIST' }],
    }),
    updateLocation: builder.mutation<
      void,
      { dealerId: number; locationId: number; locationData: Partial<DealerLocation> }
    >({
      queryFn: async ({ dealerId, locationId, locationData }) => {
        const service = getLocationService();
        await service.updateLocation(dealerId, locationId, locationData);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { locationId }) => [
        { type: 'Location', id: locationId },
        { type: 'Location', id: 'LIST' },
      ],
    }),
    deleteLocation: builder.mutation<void, string>({
      queryFn: async id => {
        const service = getLocationService();
        await service.deleteLocation(id);
        return { data: undefined };
      },
      invalidatesTags: (_, __, id) => [{ type: 'Location', id }],
    }),
  }),
});

export const {
  useGetLocationsQuery,
  useGetLocationByIdQuery,
  useAddLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  usePrefetch,
} = locationsApi;
